import { useState, useRef, useEffect } from 'react';
import {
    Send,
    Bot,
    User,
    Loader2,
    Zap,
    AlertTriangle,
    CheckCircle,
    Clock,
    ChevronDown,
    ChevronUp,
    Brain,
    Wrench,
    Sparkles,
    Workflow,
    FileText,
    Shield,
    Rocket
} from 'lucide-react';
import { AutonomyScheduler } from './AutonomyScheduler';
import { taskApi } from '../api/client';

interface AgentAction {
    tool: string;
    operation: string;
    parameters: Record<string, unknown>;
    result: 'success' | 'pending' | 'denied';
    riskScore: number;
    sourceUrl?: string;      // Deep link to the source (e.g., Splunk search URL)
    sourceLabel?: string;    // e.g., "View in Splunk", "Open in CrowdStrike"
    sourceDetails?: string;  // e.g., "index=security | search user=jsmith"
}

interface ChatMessage {
    id: string;
    role: 'user' | 'agent';
    content: string;
    thinking?: string;
    actions?: AgentAction[];
    timestamp: Date;
}

// Typing indicator component
function TypingIndicator() {
    return (
        <div className="flex items-center gap-1.5">
            <div className="typing-dot"></div>
            <div className="typing-dot"></div>
            <div className="typing-dot"></div>
        </div>
    );
}

// Thinking animation component
function ThinkingAnimation() {
    return (
        <div className="flex items-center gap-3 text-slate-400">
            <div className="relative">
                <Brain className="w-5 h-5 animate-pulse" />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-primary-400 rounded-full animate-ping"></div>
            </div>
            <span className="text-sm">Analyzing...</span>
            <TypingIndicator />
        </div>
    );
}

// Risk badge component
function RiskBadge({ score }: { score: number }) {
    let label: string;
    let className: string;

    if (score >= 70) {
        label = 'High Risk';
        className = 'risk-high';
    } else if (score >= 40) {
        label = 'Medium Risk';
        className = 'risk-medium';
    } else {
        label = 'Low Risk';
        className = 'risk-low';
    }

    return (
        <span className={`badge ${className}`}>
            {label}: {score}
        </span>
    );
}

// Collapsible tools section with source links
interface ToolsSectionProps {
    actions: AgentAction[];
}

function ToolsSection({ actions }: ToolsSectionProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [expandedAction, setExpandedAction] = useState<number | null>(null);

    const getResultIcon = (result: string) => {
        switch (result) {
            case 'success':
                return <CheckCircle className="w-4 h-4 text-success-400" />;
            case 'pending':
                return <Clock className="w-4 h-4 text-warning-400" />;
            case 'denied':
                return <AlertTriangle className="w-4 h-4 text-danger-400" />;
            default:
                return null;
        }
    };

    // Generate tool-specific URLs (mock - in production these come from backend)
    const getToolUrl = (action: AgentAction): { url: string; label: string } => {
        const toolUrls: Record<string, { baseUrl: string; label: string }> = {
            splunk: { baseUrl: 'https://splunk.company.com/search?q=', label: 'View in Splunk' },
            crowdstrike: { baseUrl: 'https://falcon.crowdstrike.com/hosts/', label: 'Open in CrowdStrike' },
            sentinel: { baseUrl: 'https://portal.azure.com/#blade/Microsoft_Azure_Security_Insights/', label: 'View in Sentinel' },
            okta: { baseUrl: 'https://company.okta.com/admin/user/', label: 'View in Okta' },
            jira: { baseUrl: 'https://company.atlassian.net/browse/', label: 'Open Ticket' },
            servicenow: { baseUrl: 'https://company.service-now.com/nav_to.do?uri=', label: 'View in ServiceNow' },
            pagerduty: { baseUrl: 'https://company.pagerduty.com/incidents/', label: 'View Incident' },
        };

        if (action.sourceUrl) {
            return { url: action.sourceUrl, label: action.sourceLabel || 'View Source' };
        }

        const toolKey = action.tool.toLowerCase();
        if (toolUrls[toolKey]) {
            const query = encodeURIComponent(JSON.stringify(action.parameters));
            return {
                url: toolUrls[toolKey].baseUrl + query,
                label: toolUrls[toolKey].label
            };
        }

        return { url: '#', label: 'View Details' };
    };

    return (
        <div className="mt-3">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-2 text-xs font-medium text-slate-400 hover:text-slate-300 transition-colors"
            >
                <Wrench className="w-3.5 h-3.5" />
                Tools Used ({actions.length})
                {isExpanded ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                )}
            </button>

            {isExpanded && (
                <div className="mt-2 space-y-2 animate-slide-down">
                    {actions.map((action, idx) => {
                        const { url, label } = getToolUrl(action);
                        const isActionExpanded = expandedAction === idx;

                        return (
                            <div
                                key={idx}
                                className="bg-slate-800/50 rounded-lg border border-slate-700/50 overflow-hidden"
                            >
                                {/* Main row */}
                                <div
                                    className="flex items-center justify-between p-2.5 cursor-pointer hover:bg-slate-800/80"
                                    onClick={() => setExpandedAction(isActionExpanded ? null : idx)}
                                >
                                    <div className="flex items-center gap-2.5">
                                        {getResultIcon(action.result)}
                                        <span className="font-mono text-xs text-slate-300">
                                            {action.tool}.{action.operation}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <a
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-xs text-primary-400 hover:text-primary-300 hover:underline"
                                        >
                                            {label} →
                                        </a>
                                        <RiskBadge score={action.riskScore} />
                                    </div>
                                </div>

                                {/* Expanded details */}
                                {isActionExpanded && (
                                    <div className="px-3 pb-3 pt-1 border-t border-slate-700/50 space-y-2">
                                        {action.sourceDetails && (
                                            <div>
                                                <span className="text-xs text-slate-500">Query:</span>
                                                <code className="block mt-1 text-xs text-slate-300 bg-slate-900 p-2 rounded font-mono overflow-x-auto">
                                                    {action.sourceDetails}
                                                </code>
                                            </div>
                                        )}
                                        {Object.keys(action.parameters).length > 0 && (
                                            <div>
                                                <span className="text-xs text-slate-500">Parameters:</span>
                                                <code className="block mt-1 text-xs text-slate-300 bg-slate-900 p-2 rounded font-mono overflow-x-auto">
                                                    {JSON.stringify(action.parameters, null, 2)}
                                                </code>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


// Simple markdown renderer
// Feature capability card
function FeatureCard({ icon: Icon, title, description, badge }: { icon: any, title: string, description: string, badge?: string }) {
    return (
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800 hover:border-primary-500/30 transition-all group cursor-default">
            <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-slate-900/50 text-primary-400 group-hover:text-primary-300 group-hover:scale-110 transition-transform">
                    <Icon className="w-5 h-5" />
                </div>
                {badge && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-900 text-slate-400 border border-slate-800">
                        {badge}
                    </span>
                )}
            </div>
            <h3 className="font-semibold text-slate-200 mb-1">{title}</h3>
            <p className="text-xs text-slate-500 leading-relaxed">{description}</p>
        </div>
    );
}

// Welcome Grid Component
function WelcomeGrid() {
    return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 overflow-y-auto animate-fade-in">
            <div className="text-center mb-8 max-w-2xl">
                <h2 className="text-3xl font-bold text-white mb-3">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-400 to-indigo-400">
                        Hyper-Automated Security
                    </span>
                </h2>
                <p className="text-slate-400">
                    Your Tier-1 Autonomous Analyst. Capable of complex investigations across your entire security stack.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-5xl">
                <FeatureCard
                    icon={Brain}
                    title="AI-Powered Reasoning"
                    description="Context-aware analysis using advanced LLMs to filter noise from signal."
                    badge="GenAI"
                />
                <FeatureCard
                    icon={Workflow}
                    title="Autonomous Execution"
                    description="Self-governing agentic loops that adapt to threats in real-time."
                    badge="Agentic"
                />
                <FeatureCard
                    icon={User}
                    title="Identity & Access"
                    description="Automated JIT access, rapid onboarding/offboarding, and stale account cleanup."
                    badge="IAM"
                />
                <FeatureCard
                    icon={Bot}
                    title="Employee Assistant"
                    description="Self-serve chatbot for instant helpdesk resolution and access requests via Slack/Teams."
                />
                <FeatureCard
                    icon={Zap}
                    title="Threat Response"
                    description="Instant auto-response to suspicious activity via IM/SMS validation."
                />
                <FeatureCard
                    icon={Shield}
                    title="Compromised Credential"
                    description="Auto-revokes tokens, suspends access, and rotates credentials upon detection."
                    badge="AutoRemediate"
                />
            </div>

            <div className="mt-8 flex gap-2">
                {/* Decorative logos */}
                {['splunk', 'crowdstrike', 'aws', 'okta', 'slack'].map(brand => (
                    <div key={brand} className="px-3 py-1.5 rounded-lg bg-slate-900/50 border border-slate-800 text-xs text-slate-500 font-mono uppercase">
                        {brand}
                    </div>
                ))}
                <div className="px-3 py-1.5 rounded-lg bg-slate-900/50 border border-slate-800 text-xs text-slate-500 font-mono">
                    +15 more
                </div>
            </div>
        </div>
    );
}

function renderMarkdown(content: string): string {
    return content
        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-white">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em class="text-slate-300">$1</em>')
        .replace(/`(.*?)`/g, '<code class="px-1.5 py-0.5 bg-slate-700/50 rounded text-primary-300 text-sm font-mono">$1</code>')
        .replace(/\n/g, '<br />')
        .replace(/- (.*?)(?=<br|$)/g, '<span class="flex items-start gap-2"><span class="text-primary-400">•</span><span>$1</span></span>');
}

export function ChatInterface() {
    const [messages, setMessages] = useState<ChatMessage[]>([]); // Empty initial state to show WelcomeGrid
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showThinking, setShowThinking] = useState<string | null>(null);
    const [deployedMsgId, setDeployedMsgId] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const deployAsTask = async (message: ChatMessage) => {
        try {
            await taskApi.submit(message.content, 'medium');
            setDeployedMsgId(message.id);
            setTimeout(() => setDeployedMsgId(null), 3000);
        } catch (err) {
            console.error('Failed to deploy as task:', err);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]);

    // Focus input on mount
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const sendMessage = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: input,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const response = await fetch('http://localhost:8080/api/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: input }),
            });

            const data = await response.json();

            const agentMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                role: 'agent',
                content: data.message,
                thinking: data.thinking,
                actions: data.actions,
                timestamp: new Date(data.timestamp),
            };

            setMessages(prev => [...prev, agentMessage]);
        } catch (error) {
            const errorMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                role: 'agent',
                content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : String(error)}. Please check the console for details.`,
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className="flex flex-col h-[calc(100vh-180px)] glass rounded-2xl overflow-hidden bg-slate-900/80 border border-slate-700/50 shadow-2xl" style={{ height: 'calc(100dvh - 180px)' }}>
            {/* Agent Card Header */}
            <div className="relative border-b border-slate-700/50 bg-slate-900/50 p-6 overflow-visible">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="relative flex items-start justify-between">
                    <div className="flex gap-5">
                        {/* Avatar */}
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 flex items-center justify-center shadow-xl shadow-black/20 group">
                            <Bot className="w-8 h-8 text-primary-400 group-hover:scale-110 transition-transform duration-500" />
                        </div>

                        {/* Identity & Capabilities */}
                        <div>
                            <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
                                ZoroAgent
                                <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-primary-500/20 text-primary-300 border border-primary-500/30">
                                    Agentic
                                </span>
                            </h2>
                            <p className="text-slate-400 text-sm mb-3 font-medium">
                                Autonomous Security Operations
                            </p>

                            <div className="flex gap-2">
                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-800 border border-slate-700/50 text-xs text-slate-300">
                                    <Workflow className="w-3.5 h-3.5 text-blue-400" />
                                    <span>Workflow</span>
                                </div>
                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-800 border border-slate-700/50 text-xs text-slate-300">
                                    <FileText className="w-3.5 h-3.5 text-purple-400" />
                                    <span>Context</span>
                                </div>
                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-800 border border-slate-700/50 text-xs text-slate-300">
                                    <Shield className="w-3.5 h-3.5 text-green-400" />
                                    <span>Secure</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Meta & Status */}
                    <div className="flex flex-col items-end gap-3">
                        <div className="flex items-center gap-2">
                            <AutonomyScheduler />
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-950 border border-slate-800 text-xs font-mono text-slate-300 shadow-sm">
                                <Sparkles className="w-3 h-3 text-yellow-400" />
                                <span>gemini-1.5-flash</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 text-xs font-medium text-emerald-400">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </span>
                            Active & Monitoring
                        </div>
                    </div>
                </div>
            </div>

            {/* Messages Area OR Welcome Grid */}
            {messages.length === 0 ? (
                <WelcomeGrid />
            ) : (
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {messages.map(message => (
                        <div
                            key={message.id}
                            className={`flex gap-4 animate-fade-in ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                        >
                            {/* Avatar */}
                            <div
                                className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center shadow-lg ${message.role === 'user'
                                    ? 'bg-gradient-to-br from-blue-500 to-blue-700 shadow-blue-500/25'
                                    : 'bg-gradient-to-br from-primary-500 to-primary-700 shadow-primary-500/25'
                                    }`}
                            >
                                {message.role === 'user' ? (
                                    <User className="w-5 h-5 text-white" />
                                ) : (
                                    <Bot className="w-5 h-5 text-white" />
                                )}
                            </div>

                            {/* Content */}
                            <div className={`flex-1 max-w-[80%] ${message.role === 'user' ? 'text-right' : ''}`}>
                                <div
                                    className={`inline-block px-4 py-3 rounded-2xl ${message.role === 'user'
                                        ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-br-md shadow-lg shadow-blue-500/25'
                                        : 'bg-slate-800/80 text-slate-200 rounded-bl-md border border-slate-700/50'
                                        }`}
                                >
                                    <div
                                        className="prose prose-sm max-w-none prose-invert"
                                        dangerouslySetInnerHTML={{
                                            __html: renderMarkdown(message.content),
                                        }}
                                    />
                                </div>

                                {/* Thinking toggle */}
                                {message.thinking && (
                                    <>
                                        <button
                                            onClick={() => setShowThinking(showThinking === message.id ? null : message.id)}
                                            className="mt-2 text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1.5 transition-colors"
                                        >
                                            <Zap className="w-3.5 h-3.5" />
                                            {showThinking === message.id ? 'Hide reasoning' : 'Show reasoning'}
                                        </button>
                                        {showThinking === message.id && (
                                            <div className="mt-2 p-3 bg-primary-500/10 rounded-lg text-sm text-primary-300 border border-primary-500/20 animate-slide-down">
                                                <div className="flex items-center gap-2 mb-2 text-xs font-medium text-primary-400">
                                                    <Brain className="w-3.5 h-3.5" />
                                                    Agent Reasoning
                                                </div>
                                                {message.thinking}
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* Actions / Tools Used */}
                                {message.actions && message.actions.length > 0 && (
                                    <ToolsSection actions={message.actions} />
                                )}

                                {/* Deploy as Task + Timestamp row */}
                                <div className="mt-2 flex items-center justify-between">
                                    <p className="text-xs text-slate-500">
                                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                    {message.role === 'agent' && (
                                        <button
                                            onClick={() => deployAsTask(message)}
                                            disabled={deployedMsgId === message.id}
                                            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all duration-300 ${deployedMsgId === message.id
                                                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                                    : 'bg-slate-700/50 text-slate-400 hover:bg-primary-500/20 hover:text-primary-300 border border-slate-600/50 hover:border-primary-500/30'
                                                }`}
                                        >
                                            {deployedMsgId === message.id ? (
                                                <><CheckCircle className="w-3.5 h-3.5" /> Deployed</>
                                            ) : (
                                                <><Rocket className="w-3.5 h-3.5" /> Deploy as Task</>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Loading indicator */}
                    {isLoading && (
                        <div className="flex gap-4 animate-fade-in">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/25">
                                <Bot className="w-5 h-5 text-white" />
                            </div>
                            <div className="bg-slate-800/80 rounded-2xl rounded-bl-md px-4 py-3 border border-slate-700/50">
                                <ThinkingAnimation />
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-slate-700/50 bg-slate-800/50">
                <div className="flex gap-3">
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask about security alerts, investigate threats, create tickets..."
                        className="input flex-1"
                        disabled={isLoading}
                    />
                    <button
                        onClick={sendMessage}
                        disabled={!input.trim() || isLoading}
                        className="btn btn-primary px-5 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center gap-2"
                    >
                        {isLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <Send className="w-5 h-5" />
                        )}
                    </button>
                </div>
                <p className="mt-2 text-xs text-slate-500 text-center">
                    Press Enter to send • ZoroAgent responds in real-time
                </p>
            </div>
        </div>
    );
}
